import { useState, useEffect, useCallback } from "react";
import { useQuestionnaireResponse } from "../../../../../hooks/useQuestionnaireResponse";
import { usePatientData } from "../../../../../hooks/usePatientData";
import {
  getImcRelatedLinkIds,
  calculateImc,
  formatImcForField,
  buildPreFilledAnswers,
  validateQuestionnaireField,
  shouldHideQuestionnaireItem,
  type PatientDataForPreFill,
} from "../utils/questionnaireFormUtils";

interface UseQuestionnaireFormParams {
  questionnaire: any;
  patientId?: string;
  encounterId?: string;
}

export function useQuestionnaireForm({
  questionnaire,
  patientId,
  encounterId,
}: UseQuestionnaireFormParams) {
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const {
    submitQuestionnaireResponse,
    loading: submitLoading,
    error: submitError,
    clearError,
  } = useQuestionnaireResponse({ patientId, encounterId });

  const patientData = usePatientData();

  // Pré-preenchimento quando questionnaire ou patientData mudam
  useEffect(() => {
    if (!questionnaire?.item) return;
    const preFilledAnswers = buildPreFilledAnswers(questionnaire, patientData as PatientDataForPreFill);
    if (Object.keys(preFilledAnswers).length > 0) {
      setAnswers((prev) => ({ ...prev, ...preFilledAnswers }));
    }
  }, [questionnaire, patientData]);

  // Pré-preenchimento inicial (mount) com pequeno delay para garantir dados do contexto
  useEffect(() => {
    const timer = setTimeout(() => {
      if (questionnaire?.item && patientData) {
        const preFilledAnswers = buildPreFilledAnswers(questionnaire, patientData as PatientDataForPreFill);
        if (Object.keys(preFilledAnswers).length > 0) {
          setAnswers(preFilledAnswers);
        }
      }
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const handleAnswerChange = useCallback(
    (linkId: string, value: any) => {
      setAnswers((prev) => {
        const next = { ...prev, [linkId]: value };
        const { alturaLinkId, pesoLinkId, imcLinkId } = getImcRelatedLinkIds(questionnaire);
        if (
          imcLinkId &&
          alturaLinkId &&
          pesoLinkId &&
          (linkId === alturaLinkId || linkId === pesoLinkId)
        ) {
          const alturaRaw = linkId === alturaLinkId ? value : prev[alturaLinkId];
          const pesoRaw = linkId === pesoLinkId ? value : prev[pesoLinkId];
          const alturaNum = Number(alturaRaw);
          const pesoNum = Number(pesoRaw);
          if (
            !Number.isNaN(alturaNum) &&
            alturaNum > 0 &&
            !Number.isNaN(pesoNum) &&
            pesoNum > 0
          ) {
            next[imcLinkId] = formatImcForField(calculateImc(pesoNum, alturaNum));
          }
        }
        return next;
      });
      setTouched((prev) => ({ ...prev, [linkId]: true }));
    },
    [questionnaire]
  );

  const validateForm = useCallback((): { isValid: boolean; firstErrorLinkId?: string } => {
    const newErrors: Record<string, string> = {};
    let isValid = true;
    let firstErrorLinkId: string | undefined;

    const validateItems = (items: any[]) => {
      items.forEach((item) => {
        if (shouldHideQuestionnaireItem(item)) {
          return;
        }

        const error = validateQuestionnaireField(item, answers[item.linkId]);
        if (error) {
          newErrors[item.linkId] = error;
          if (!firstErrorLinkId) firstErrorLinkId = item.linkId;
          isValid = false;
        }
        if (item.item) validateItems(item.item);
      });
    };

    validateItems(questionnaire?.item ?? []);
    setErrors(newErrors);
    return { isValid, firstErrorLinkId };
  }, [questionnaire?.item, answers]);

  const markAllTouched = useCallback(() => {
    const newTouched: Record<string, boolean> = {};
    const mark = (items: any[]) => {
      items.forEach((item) => {
        newTouched[item.linkId] = true;
        if (item.item) mark(item.item);
      });
    };
    mark(questionnaire?.item ?? []);
    setTouched(newTouched);
  }, [questionnaire?.item]);

  const handleSubmit = useCallback(
    async (
      onSuccess: () => void,
      onRefresh?: () => void,
      onValidationFailed?: (firstErrorLinkId: string) => void
    ) => {
      const { isValid, firstErrorLinkId } = validateForm();
      if (!isValid) {
        markAllTouched();
        if (firstErrorLinkId) {
          requestAnimationFrame(() => onValidationFailed?.(firstErrorLinkId));
        }
        return;
      }
      try {
        const success = await submitQuestionnaireResponse(
          questionnaire.id,
          answers,
          questionnaire
        );
        if (success) onSuccess();
      } catch (error) {
        console.error("Erro ao enviar QuestionnaireResponse:", error);
      } finally {
        onRefresh?.();
      }
    },
    [
      validateForm,
      markAllTouched,
      submitQuestionnaireResponse,
      questionnaire,
      answers,
    ]
  );

  return {
    answers,
    errors,
    touched,
    setErrors,
    handleAnswerChange,
    validateField: validateQuestionnaireField,
    shouldHideItem: shouldHideQuestionnaireItem,
    handleSubmit,
    validateForm,
    submitLoading,
    submitError,
    clearError,
  };
}
